// The session as an orbit around the device (docs/brand.md, Signature visual): the device is the
// filled focus; time runs from apoapsis (the start, left) to periapsis along the solid half; the
// dotted half is after the last event. Events are numbered ticks; a hang is a thick arc in the
// degraded color; the periapsis point is the only accent.
import { FOCUS, ORBIT, orbitArc, orbitPoint } from '../lib/session'

export interface OrbitTick {
  fraction: number
  /** For a hang: where it ends on the orbit. */
  endFraction?: number
}

const CENTER = { x: ORBIT.cx, y: ORBIT.cy }

function labelAt(fraction: number) {
  const p = orbitPoint(fraction)
  const dx = p.x - CENTER.x
  const dy = p.y - CENTER.y
  const len = Math.hypot(dx / ORBIT.rx, dy / ORBIT.ry) || 1
  // Outward from the ellipse, scaled so labels sit about 20 units off the line.
  const nx = dx / ORBIT.rx / len
  const ny = dy / ORBIT.ry / len
  return { x: p.x + nx * 21, y: p.y + ny * 21 + 5 }
}

/**
 * Where each tick's number goes: at the tick, nudged along the orbit so close ticks keep
 * readable labels. The ticks themselves stay where their time puts them.
 */
export function labelFractions(fractions: number[], gap = 0.08): number[] {
  const out = [...fractions]
  for (let i = 1; i < out.length; i++)
    out[i] = Math.max(out[i] as number, (out[i - 1] as number) + gap)
  const over = (out.at(-1) ?? 0) - 1
  if (over > 0) {
    out[out.length - 1] = 1
    for (let i = out.length - 2; i >= 0; i--)
      out[i] = Math.min(out[i] as number, (out[i + 1] as number) - gap)
  }
  return out.map((f) => Math.min(1, Math.max(0, f)))
}

export function Orbit({
  ticks,
  hangs,
  periapsis,
  selected,
  deviceLabel,
  centerTitle,
  centerSub,
}: {
  ticks: OrbitTick[]
  hangs: OrbitTick[]
  periapsis: number
  selected: number
  deviceLabel: string
  centerTitle?: string
  centerSub?: string
}) {
  const labels = labelFractions(ticks.map((t) => t.fraction))
  const left = orbitPoint(0)
  const right = orbitPoint(1)
  const dot = orbitPoint(ticks[selected]?.fraction ?? 1)
  const peri = ticks[periapsis] ? orbitPoint(ticks[periapsis].fraction) : undefined
  return (
    <svg className="orbit" viewBox="0 0 580 290" aria-hidden="true">
      <path
        className="o-after"
        d={`M ${right.x} ${right.y} A ${ORBIT.rx} ${ORBIT.ry} 0 0 1 ${left.x} ${left.y}`}
      />
      <path className="o-path" d={orbitArc(0, 1)} />
      {hangs.map((h) => (
        <path
          key={`${h.fraction}-${h.endFraction}`}
          className="o-hang"
          d={orbitArc(h.fraction, Math.max(h.fraction + 0.004, h.endFraction ?? h.fraction))}
        />
      ))}
      <circle className="o-focus" cx={FOCUS.x} cy={FOCUS.y} r="10" />
      <text className="o-label" x={FOCUS.x} y={FOCUS.y + 33}>
        {deviceLabel}
      </text>
      {ticks.map((t, i) => {
        const p = orbitPoint(t.fraction)
        const l = labelAt(labels[i] ?? t.fraction)
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: ticks are positional
          <g key={i}>
            <circle className="o-tick" cx={p.x} cy={p.y} r="4.5" />
            <text className="o-label" x={l.x} y={l.y}>
              {i + 1}
            </text>
          </g>
        )
      })}
      {peri ? <circle className="o-ring" cx={peri.x} cy={peri.y} r="13" /> : null}
      <circle
        className="o-dot orbit-dot"
        cx="0"
        cy="0"
        r="8"
        style={{ transform: `translate(${dot.x}px, ${dot.y}px)` }}
      />
      {centerTitle ? (
        <text className="o-time" x={CENTER.x} y={CENTER.y + 8}>
          {centerTitle}
        </text>
      ) : null}
      {centerSub ? (
        <text className="o-name" x={CENTER.x} y={CENTER.y + 34}>
          {centerSub.length > 44 ? `${centerSub.slice(0, 43)}…` : centerSub}
        </text>
      ) : null}
    </svg>
  )
}
